"use client"

import type { ComponentProps } from "react"
import { useTranslations } from "@/lib/intl/use-translations"
import { BookingCta } from "@/components/ui/booking-cta"
import type { ServiceAvailability } from "@/lib/service-availability"
import { isWaitlistState } from "@/lib/service-availability"
import { cn } from "@/lib/utils"

type BookingCtaProps = ComponentProps<typeof BookingCta>

/**
 * Booking CTA driven by the single `ServiceAvailability` verdict
 * (`resolveServiceAvailability`), resolved once per page by the caller so the
 * path/slug join runs a single time. One state in, one coherent CTA out:
 *
 *   not_available        → a short notice in place of any CTA; with
 *                          `waitlistWhenUnavailable`, the notice plus a
 *                          waitlist button.
 *   coming_soon/waitlist → the booking CTA, forced to the waitlist modal
 *                          (`forceWaitlist`) so it can't silently book when the
 *                          per-service status is coming-soon but the category is open.
 *   bookable             → the normal booking CTA.
 *
 * `tone`: "light" (default) for light backgrounds, "dark" for photo/dark heroes.
 */
export function ServiceBookingCta({
  availability,
  className,
  children,
  tone = "light",
  waitlistWhenUnavailable = false,
  ...bookingProps
}: BookingCtaProps & {
  availability: ServiceAvailability
  tone?: "light" | "dark"
  waitlistWhenUnavailable?: boolean
}) {
  const ut = useTranslations("ui")

  if (availability === "not_available") {
    const notice = (
      <p
        className={cn(
          "type-body-sm max-w-sm text-center font-medium",
          tone === "dark" ? "text-white/80" : "text-neutral-600"
        )}
      >
        {ut("testUnavailableNotice")}
      </p>
    )
    if (!waitlistWhenUnavailable) return notice
    return (
      <div
        className={cn(
          "flex flex-col gap-3",
          tone === "dark" ? "items-center" : "items-start"
        )}
      >
        {notice}
        <BookingCta className={className} forceWaitlist {...bookingProps}>
          {children}
        </BookingCta>
      </div>
    )
  }

  return (
    <BookingCta
      className={className}
      forceWaitlist={isWaitlistState(availability)}
      {...bookingProps}
    >
      {children}
    </BookingCta>
  )
}
